import { streamEvents } from "../client.js";
import {
  resolveRunState,
  runStateFromEvent
} from "../domain/conversation.js";
import { needsTurnDetailSnapshotRefresh } from "../domain/turn-detail.js";
import { refreshTurnDetail } from "./snapshot.js";
import { clearTurnDetailLiveState } from "./state.js";

const liveDeps = {
  streamEvents,
  setTimeout: globalThis.setTimeout.bind(globalThis),
  clearTimeout: globalThis.clearTimeout.bind(globalThis),
  refreshTurnDetail
};

export function startTurnDetailLive(state, apiKey, turnID, options = {}) {
  const deps = { ...liveDeps, ...(options.deps || {}) };
  stopTurnDetailLive(state, deps);
  if (!apiKey || !turnID || state.notFound || !state.turn) {
    state.streamState = "idle";
    options.render?.();
    return;
  }

  const controller = new AbortController();
  state.live.streamAbort = controller;
  state.streamState = "connecting";
  options.render?.();

  void deps.streamEvents(apiKey, `/v1/turns/${encodeURIComponent(turnID)}/events?stream=1`, {
    signal: controller.signal,
    onEvent(next) {
      mergeTurnDetailEvent(state, turnID, next.payload, options);
      if (needsTurnDetailSnapshotRefresh(next.payload.event_type)) {
        scheduleTurnDetailSnapshotRefresh(state, apiKey, turnID, options, deps);
      }
      options.render?.();
    }
  }).then(() => {
    if (!controller.signal.aborted) {
      state.streamState = "closed";
      options.render?.();
    }
  }).catch((error) => {
    if (controller.signal.aborted) {
      return;
    }
    state.streamState = "error";
    options.setStatus?.(error.message, "error");
    options.render?.();
  });
}

export function stopTurnDetailLive(state, deps = liveDeps) {
  clearTurnDetailLiveState(state, { clearTimeout: deps.clearTimeout });
}

export function mergeTurnDetailEvent(state, turnID, event, options = {}) {
  if (!event || state.eventSeqs.has(event.seq)) {
    return;
  }
  state.eventSeqs.add(event.seq);
  state.events = [...state.events, event].sort((left, right) => left.seq - right.seq);
  state.streamState = "connected";
  const nextState = runStateFromEvent(event);
  if (state.turn && state.turn.id === turnID && nextState) {
    state.turn = { ...state.turn, state: resolveRunState(state.turn.state, nextState) };
  }
  if (nextState) {
    options.onTurnStateChange?.(turnID, nextState);
  }
}

export function scheduleTurnDetailSnapshotRefresh(state, apiKey, turnID, options = {}, deps = liveDeps) {
  if (state.live.snapshotTimer) {
    deps.clearTimeout(state.live.snapshotTimer);
  }
  state.live.snapshotTimer = deps.setTimeout(() => {
    void deps.refreshTurnDetail(state, apiKey, turnID, {
      includeEvents: false,
      request: options.request,
      signal: options.signal,
      onTurnMerged: options.onTurnMerged
    }).then((changed) => {
      if (changed) {
        options.render?.();
      }
    }).catch((error) => {
      options.setStatus?.(error.message, "error");
      options.render?.();
    });
  }, 250);
}
